// src/components/NotificationBell.jsx
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { NotificationContext } from '../context/NotificationContext';

const TYPE_ICON = {
    created:       '🎫',
    bumped:        '💬',
    overdue:       '⏰',
    cancelacion:   '⚠️',
    authorization: '📋',
    closed:        '⭐',
    guard:         '🛡️',
};

function fmtTime(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
}

// Panel anclado a la campana en escritorio; en teléfono va fijo a lo ancho
// de la pantalla (anclado a la campana se salía por la izquierda).
const NB_CSS = `
.nb-panel { position: absolute; top: 100%; right: 0; margin-top: 8px; width: 340px; }
@media (max-width: 640px) {
    .nb-panel { position: fixed; top: 56px; left: 8px; right: 8px; width: auto; margin-top: 0; max-height: 70vh !important; }
}
`;

export default function NotificationBell() {
    const { notifications, unreadCount, markAllRead, clearAll, markRead } = useContext(NotificationContext);
    const [open, setOpen] = useState(false);
    const panelRef = useRef(null);
    const navigate = useNavigate();

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const handleClick = (notif) => {
        markRead(notif.id);
        if (notif.ticketId) {
            navigate(`/tickets/${notif.ticketId}`);
        } else if (notif.link) {
            navigate(notif.link);
        }
        setOpen(false);
    };

    return (
        <div style={{ position: 'relative' }} ref={panelRef}>
            <style>{NB_CSS}</style>
            <Button
                variant="outline-light"
                size="sm"
                onClick={() => setOpen(v => !v)}
                title="Notificaciones"
                style={{ borderRadius: 999, position: 'relative', padding: '4px 10px' }}
            >
                🔔
                {unreadCount > 0 && (
                    <span style={{
                        position: 'absolute', top: -4, right: -4,
                        background: '#ef4444', color: '#fff',
                        borderRadius: '50%', width: 17, height: 17,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 10, fontWeight: 700, lineHeight: 1,
                        border: '2px solid #1e293b',
                    }}>
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </Button>

            {open && (
                <div className="nb-panel" style={{
                    maxHeight: 440, overflowY: 'auto',
                    background: 'var(--c-card)',
                    border: '1px solid var(--c-border)',
                    borderRadius: 12,
                    boxShadow: '0 8px 24px rgba(0,0,0,.14)',
                    zIndex: 9999,
                    color: 'var(--c-text-1)',
                }}>
                    {/* Header */}
                    <div style={{
                        padding: '12px 16px',
                        borderBottom: '1px solid var(--c-card-header-border)',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        flexShrink: 0,
                    }}>
                        <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--c-text-1)' }}>
                            Notificaciones
                        </span>
                        <div style={{ display: 'flex', gap: 8 }}>
                            {unreadCount > 0 && (
                                <button
                                    onClick={markAllRead}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#2563eb', padding: 0 }}
                                >
                                    Marcar leídas
                                </button>
                            )}
                            {notifications.length > 0 && (
                                <button
                                    onClick={clearAll}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--c-text-4)', padding: 0 }}
                                >
                                    Limpiar
                                </button>
                            )}
                        </div>
                    </div>

                    {/* List */}
                    {notifications.length === 0 ? (
                        <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--c-text-4)', fontSize: 13 }}>
                            Sin notificaciones
                        </div>
                    ) : (
                        notifications.map(n => (
                            <div
                                key={n.id}
                                onClick={() => handleClick(n)}
                                style={{
                                    padding: '10px 16px',
                                    borderBottom: '1px solid var(--c-card-header-border)',
                                    cursor: 'pointer',
                                    background: n.read ? 'transparent' : 'var(--c-status-open-bg)',
                                    display: 'flex', gap: 10, alignItems: 'flex-start',
                                    transition: 'background 0.15s',
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = 'var(--c-msg-bg)'}
                                onMouseLeave={e => e.currentTarget.style.background = n.read ? 'transparent' : 'var(--c-status-open-bg)'}
                            >
                                <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>
                                    {TYPE_ICON[n.type] || '🔔'}
                                </span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <p style={{ margin: 0, fontSize: 13, color: 'var(--c-text-1)', lineHeight: 1.4 }}>
                                        {n.message}
                                    </p>
                                    <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--c-text-4)' }}>
                                        {fmtTime(n.timestamp)}
                                    </p>
                                </div>
                                {!n.read && (
                                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#2563eb', flexShrink: 0, marginTop: 5 }} />
                                )}
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}
